import React, { useState, useEffect, useRef } from 'react'
import SvgTriangleUIIcon from 'Pods/Shared/SvgTriangleUIIcon'
import { arrayOf, shape, string } from 'prop-types'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

const availableLocales = [
  {
    locale: 'de', path: '/de', name: 'Deutsch', flag: '🇩🇪', description: 'German',
  },
  {
    locale: 'en', path: '/', name: 'English', flag: '🇺🇸', description: 'English',
  },
  {
    locale: 'es', path: '/es', name: 'Español', flag: '🇪🇸', description: 'Spanish',
  },
  {
    locale: 'fr', path: '/fr', name: 'Français', flag: '🇫🇷', description: 'French',
  },
  {
    locale: 'pt', path: '/pt', name: 'Português', flag: '🇵🇹', description: 'Portuguese',
  },
  {
    locale: 'ru', path: '/ru', name: 'Русский', flag: '🇷🇺', description: 'Russian',
  },
  {
    locale: 'ja', path: '/ja', name: '日本語', flag: '🇯🇵', description: 'Japanese',
  },
  {
    locale: 'zh-CN', path: '/zh-cn', name: '中文', flag: '🇨🇳', description: 'Chinese',
  },
]

function stripCurrentLocale(pathname, currentLocalePath) {
  if (!pathname) {
    return ''
  }

  if (!currentLocalePath || currentLocalePath === '/') {
    return pathname
  }

  if (pathname === currentLocalePath) {
    return '/'
  }

  if (pathname.startsWith(`${currentLocalePath}/`)) {
    return pathname.slice(currentLocalePath.length) || '/'
  }

  return pathname
}

function buildLocalePath({
  path,
  currentPathname,
  currentQuery,
  currentLocalePath,
}) {
  const normalizedLocalePath = path === '/' ? '' : path
  const localeStrippedPathname = stripCurrentLocale(currentPathname, currentLocalePath)
  const normalizedPathname = (!localeStrippedPathname || localeStrippedPathname === '/') ? '' : localeStrippedPathname
  const pathnameWithSlash = normalizedPathname.startsWith('/') ? normalizedPathname : `/${normalizedPathname}`
  const combinedPath = `${normalizedLocalePath}${pathnameWithSlash}`
  const sanitizedPath = combinedPath
    .replace(/\/+/g, '/')
    .replace(/\/$/, '')
    || '/'
  const query = currentQuery ? `?${currentQuery}` : ''

  return `${sanitizedPath}${query}`
}

function getBrowserLocation() {
  if (typeof window === 'undefined') {
    return { pathname: '/', search: '' }
  }

  return {
    pathname: window.location.pathname || '/',
    search: window.location.search || '',
  }
}

function LocaleSelector({
  activeLocale,
  currentPathname = '/',
  currentQuery = '',
  localeUrls = EMPTY_ARRAY,
}) {
  const container = useRef()
  const [isOpen, setIsOpen] = useState(false)
  const [hasHydrated, setHasHydrated] = useState(false)
  const activeLocaleItem = availableLocales.find(item => item.locale === activeLocale)
    || availableLocales.find(item => item.locale === 'en')
    || availableLocales[0]
  const currentLocalePath = activeLocaleItem?.path || ''
  const browserLocation = getBrowserLocation()
  const normalizedBrowserPathname = browserLocation.pathname || '/'
  const normalizedBrowserQuery = browserLocation.search.replace(/^\?/, '')
  // Pre-hydration must render identically on server and client, so it can only read props
  // (the server has no window). Reading window.location here diverged the first client render
  // from the SSR markup (Server "/de" vs Client "/de/profile/models"). hasHydrated does the swap.
  const preHydrationPathname = currentPathname || '/'
  const preHydrationQuery = currentQuery || ''
  const effectivePathname = hasHydrated ? normalizedBrowserPathname : preHydrationPathname
  const effectiveQuery = hasHydrated ? normalizedBrowserQuery : preHydrationQuery

  useEffect(() => {
    setHasHydrated(true)
  }, [])

  useEffect(() => {
    function handleMouseDown(e) {
      if ((container.current && container.current.contains(e.target))) {
        return false
      }

      setIsOpen(false)

      return true
    }

    document.addEventListener('mousedown', handleMouseDown)

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [])

  function renderSelector(item) {
    return (
      <button
        className="locale-selector--selector"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="flag">
          {item.flag}
        </span>
        {item.name}
        <SvgTriangleUIIcon type="unfoldMore" color="light" size="small" />
      </button>
    )
  }
  function renderItem(item) {
    const serverUrl = localeUrls.find(entry => entry.locale === item.locale)?.url
    const href = serverUrl || buildLocalePath({
      path: item.path,
      currentPathname: effectivePathname,
      currentQuery: effectiveQuery,
      currentLocalePath,
    })

    return (
      <a
        key={item.path}
        className="locale-selector--dropdown--link"
        data-track-label="Language"
        data-track-text={item.description}
        href={href}
      >
        <span className="flag">
          {item.flag}
        </span>
        {item.name}
      </a>
    )
  }
  const dropdownItems = availableLocales.filter(item => item.locale !== activeLocale).map(renderItem)
  return (
    <div
      ref={container}
      className={`locale-selector ${isOpen ? 'locale-selector_open' : ''}`}
    >
      {renderSelector(activeLocaleItem)}
      <div
        className="locale-selector--dropdown"
      >
        {dropdownItems}
      </div>
    </div>
  )
}

LocaleSelector.propTypes = {
  activeLocale: string.isRequired,
  currentPathname: string,
  currentQuery: string,
  localeUrls: arrayOf(shape({ locale: string, url: string })),
}

export default LocaleSelector
